import React, { useState, useEffect } from 'react'
import {
  KeyIcon,
  UserGroupIcon,
  PlusIcon,
  TrashIcon,
  PencilSquareIcon,
  CheckCircleIcon,
  ClockIcon,
  ClipboardDocumentIcon,
  SparklesIcon,
  XMarkIcon,
  ShieldCheckIcon,
  SunIcon,
  PaperAirplaneIcon
} from '@heroicons/react/24/outline'
import api from '@/utils/api'
import { useAuth } from '@/contexts/AuthContext'
import { formatHoursAndMinutes } from '@/utils/jalali'
import { ConfirmModal, AlertModal } from '@/components/ConfirmModal'

interface InvitationCode {
  id: number
  code: string
  default_work_start: string
  default_daily_hours: number
  max_uses: number
  used_count: number
  is_active: boolean
  created_at: string
}

interface AdminUser {
  id: number
  name: string
  username?: string
  telegram_id?: number
  role: string
  is_active: boolean
  work_start: string
  daily_hours: number
  created_at: string
  month_worked_hours: number
  today_status: string
}

export const AdminPanel: React.FC = () => {
  const { user: currentUser } = useAuth()
  const [activeTab, setActiveTab] = useState<'invites' | 'users'>('invites')
  
  const [invitations, setInvitations] = useState<InvitationCode[]>([])
  const [users, setUsers] = useState<AdminUser[]>([])
  const [loading, setLoading] = useState(true)
  
  // Create Invite Modal
  const [showCreateInvite, setShowCreateInvite] = useState(false)
  const [inviteHours, setInviteHours] = useState('8.0')
  const [inviteStart, setInviteStart] = useState('09:15')
  const [inviteMaxUses, setInviteMaxUses] = useState(1)
  const [creatingInvite, setCreatingInvite] = useState(false)

  // Edit User Modal
  const [editingUser, setEditingUser] = useState<AdminUser | null>(null)
  const [editName, setEditName] = useState('')
  const [editStart, setEditStart] = useState('09:15')
  const [editHours, setEditHours] = useState(8.0)
  const [editIsActive, setEditIsActive] = useState(true)
  const [savingUser, setSavingUser] = useState(false)

  // Modals
  const [confirmDialog, setConfirmDialog] = useState<{
    isOpen: boolean
    title: string
    message: string
    confirmVariant?: 'danger' | 'primary' | 'warning'
    onConfirm: () => void
  }>({
    isOpen: false,
    title: '',
    message: '',
    confirmVariant: 'danger',
    onConfirm: () => {}
  })

  const [alertDialog, setAlertDialog] = useState<{
    isOpen: boolean
    title?: string
    message: string
    type?: 'success' | 'error' | 'info'
  }>({
    isOpen: false,
    title: '',
    message: '',
    type: 'info'
  })

  const fetchData = async () => {
    setLoading(true)
    try {
      const [invRes, usersRes] = await Promise.allSettled([
        api.get('/admin/invitations'),
        api.get('/admin/users')
      ])

      if (invRes.status === 'fulfilled' && Array.isArray(invRes.value.data)) {
        setInvitations(invRes.value.data)
      } else {
        setInvitations([])
      }

      if (usersRes.status === 'fulfilled' && Array.isArray(usersRes.value.data)) {
        setUsers(usersRes.value.data)
      } else {
        setUsers([])
      }
    } catch (err) {
      console.error('Error fetching admin data:', err)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchData()
  }, [])

  // Create invite code
  const handleCreateInvite = async (e: React.FormEvent) => {
    e.preventDefault()
    setCreatingInvite(true)
    try {
      const res = await api.post('/admin/invitations', {
        default_work_start: inviteStart,
        default_daily_hours: parseFloat(inviteHours),
        max_uses: inviteMaxUses
      })
      setShowCreateInvite(false)
      await fetchData()
      setAlertDialog({
        isOpen: true,
        type: 'success',
        title: 'کد دعوت ایجاد شد',
        message: `کد دعوت جدید: ${res.data.code}\nلینک ثبت‌نام: https://att.mobinmoghimi.ir/register?code=${res.data.code}`
      })
    } catch (err: any) {
      setAlertDialog({
        isOpen: true,
        type: 'error',
        title: 'خطا',
        message: err.response?.data?.detail || 'خطا در ایجاد کد دعوت'
      })
    } finally {
      setCreatingInvite(false)
    }
  }

  // Delete invite code
  const handleDeleteInvite = (id: number) => {
    setConfirmDialog({
      isOpen: true,
      title: 'حذف کد دعوت',
      message: 'آیا از حذف این کد دعوت اطمینان دارید؟',
      confirmVariant: 'danger',
      onConfirm: async () => {
        setConfirmDialog(prev => ({ ...prev, isOpen: false }))
        try {
          await api.delete(`/admin/invitations/${id}`)
          await fetchData()
        } catch (err: any) {
          setAlertDialog({
            isOpen: true,
            type: 'error',
            title: 'خطا',
            message: err.response?.data?.detail || 'خطا در حذف کد'
          })
        }
      }
    })
  }

  // Delete user
  const handleDeleteUser = (userToDelete: AdminUser) => {
    setConfirmDialog({
      isOpen: true,
      title: 'حذف کاربر',
      message: `آیا از حذف حساب کاربری «${userToDelete.name}» اطمینان کامل دارید؟ تمامی سوابق تردد، مرخصی و داده‌های این کاربر برای همیشه حذف خواهند شد.`,
      confirmVariant: 'danger',
      onConfirm: async () => {
        setConfirmDialog(prev => ({ ...prev, isOpen: false }))
        try {
          await api.delete(`/admin/users/${userToDelete.id}`)
          await fetchData()
          setAlertDialog({
            isOpen: true,
            type: 'success',
            title: 'حذف شد',
            message: `حساب کاربری «${userToDelete.name}» با موفقیت حذف گردید.`
          })
        } catch (err: any) {
          setAlertDialog({
            isOpen: true,
            type: 'error',
            title: 'خطا در حذف کاربر',
            message: err.response?.data?.detail || 'خطا در حذف کاربر'
          })
        }
      }
    })
  }

  // Copy code or link
  const handleCopyCode = (code: string) => {
    const link = `https://att.mobinmoghimi.ir/register?code=${code}`
    navigator.clipboard.writeText(link)
    setAlertDialog({
      isOpen: true,
      type: 'success',
      title: 'کپی شد',
      message: `لینک ثبت‌نام برای کد ${code} در کلیپ‌بورد کپی شد.`
    })
  }

  // Edit User
  const openEditUser = (user: AdminUser) => {
    setEditingUser(user)
    setEditName(user.name)
    setEditStart(user.work_start || '09:15')
    setEditHours(user.daily_hours || 8.0)
    setEditIsActive(user.is_active)
  }

  const handleSaveUser = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!editingUser) return
    setSavingUser(true)
    try {
      await api.patch(`/admin/users/${editingUser.id}`, {
        name: editName,
        work_start: editStart,
        daily_hours: editHours,
        is_active: editIsActive
      })
      setEditingUser(null)
      await fetchData()
      setAlertDialog({
        isOpen: true,
        type: 'success',
        title: 'ذخیره شد',
        message: 'اطلاعات کاربر با موفقیت بروزرسانی شد.'
      })
    } catch (err: any) {
      setAlertDialog({
        isOpen: true,
        type: 'error',
        title: 'خطا',
        message: err.response?.data?.detail || 'خطا در ویرایش اطلاعات کاربر'
      })
    } finally {
      setSavingUser(false)
    }
  }

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'working':
        return <span className="px-2 py-0.5 rounded-lg text-[11px] font-bold bg-emerald-100 dark:bg-emerald-950/60 text-emerald-700 dark:text-emerald-300">در حال کار</span>
      case 'finished':
        return <span className="px-2 py-0.5 rounded-lg text-[11px] font-bold bg-blue-100 dark:bg-blue-950/60 text-blue-700 dark:text-blue-300">خروج ثبت شده</span>
      case 'leave':
        return <span className="px-2 py-0.5 rounded-lg text-[11px] font-bold bg-amber-100 dark:bg-amber-950/60 text-amber-700 dark:text-amber-300">مرخصی</span>
      default:
        return <span className="px-2 py-0.5 rounded-lg text-[11px] font-bold bg-slate-100 dark:bg-slate-800 text-slate-500">عدم ثبت ورود</span>
    }
  }

  return (
    <div className="space-y-6 pb-20 max-w-4xl mx-auto">
      {/* Header */}
      <div className="bg-white dark:bg-slate-800 rounded-3xl p-6 shadow-sm border border-slate-100 dark:border-slate-700/60 flex flex-col sm:flex-row items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="p-3 bg-purple-500/10 text-purple-600 dark:text-purple-400 rounded-2xl">
            <ShieldCheckIcon className="w-6 h-6" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-slate-800 dark:text-white">
              پنل مدیریت سیستم
            </h1>
            <p className="text-xs text-slate-500 dark:text-slate-400">
              مدیریت کدهای دعوت، مشاهده، ویرایش و حذف کاربران
            </p>
          </div>
        </div>

        {/* Tab switcher */}
        <div className="flex items-center gap-2 bg-slate-100 dark:bg-slate-900 p-1.5 rounded-2xl">
          <button
            onClick={() => setActiveTab('invites')}
            className={`px-4 py-2 rounded-xl text-xs font-bold transition ${
              activeTab === 'invites'
                ? 'bg-white dark:bg-slate-800 text-slate-800 dark:text-white shadow-sm'
                : 'text-slate-500 hover:text-slate-800 dark:hover:text-white'
            }`}
          >
            کدهای دعوت
          </button>
          <button
            onClick={() => setActiveTab('users')}
            className={`px-4 py-2 rounded-xl text-xs font-bold transition ${
              activeTab === 'users'
                ? 'bg-white dark:bg-slate-800 text-slate-800 dark:text-white shadow-sm'
                : 'text-slate-500 hover:text-slate-800 dark:hover:text-white'
            }`}
          >
            لیست کاربران ({users.length})
          </button>
        </div>
      </div>

      {/* Tab 1: Invitations */}
      {activeTab === 'invites' && (
        <div className="bg-white dark:bg-slate-800 rounded-3xl p-6 shadow-sm border border-slate-100 dark:border-slate-700/60 space-y-4">
          <div className="flex items-center justify-between pb-3 border-b border-slate-100 dark:border-slate-700">
            <h2 className="text-base font-bold text-slate-800 dark:text-white flex items-center gap-2">
              <KeyIcon className="w-5 h-5 text-purple-600" />
              کدهای دعوت برای ثبت‌نام کاربران
            </h2>
            <button
              onClick={() => setShowCreateInvite(true)}
              className="flex items-center gap-1.5 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-xl text-xs font-bold transition shadow-sm"
            >
              <PlusIcon className="w-4 h-4" />
              ایجاد کد دعوت جدید
            </button>
          </div>

          {invitations.length === 0 ? (
            <p className="text-xs text-slate-400 text-center py-8">
              هنوز کد دعوتی ایجاد نشده است. برای ثبت‌نام کاربران جدید، یک کد ایجاد کنید.
            </p>
          ) : (
            <div className="space-y-3">
              {invitations.map(inv => (
                <div
                  key={inv.id}
                  className={`flex items-center justify-between p-4 rounded-2xl border transition ${
                    inv.is_active
                      ? 'bg-slate-50 dark:bg-slate-900/40 border-slate-200 dark:border-slate-700'
                      : 'bg-slate-50/50 dark:bg-slate-900/20 border-slate-100 dark:border-slate-800 opacity-60'
                  }`}
                >
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-sm font-extrabold tracking-wider text-purple-600 dark:text-purple-400">
                        {inv.code}
                      </span>
                      {inv.is_active ? (
                        <span className="px-2 py-0.5 rounded-md text-[10px] font-bold bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300">
                          معتبر ({inv.used_count}/{inv.max_uses})
                        </span>
                      ) : (
                        <span className="px-2 py-0.5 rounded-md text-[10px] font-bold bg-slate-200 text-slate-600 dark:bg-slate-800 dark:text-slate-400">
                          استفاده شده / منقضی
                        </span>
                      )}
                    </div>
                    <p className="text-[11px] text-slate-400">
                      شروع مقرر: {inv.default_work_start} | سقف روزانه: {inv.default_daily_hours} ساعت
                    </p>
                  </div>

                  <div className="flex items-center gap-2">
                    {inv.is_active && (
                      <button
                        onClick={() => handleCopyCode(inv.code)}
                        className="p-2 text-purple-600 hover:bg-purple-50 dark:hover:bg-purple-950/40 rounded-xl transition"
                        title="کپی لینک ثبت‌نام"
                      >
                        <ClipboardDocumentIcon className="w-5 h-5" />
                      </button>
                    )}
                    <button
                      onClick={() => handleDeleteInvite(inv.id)}
                      className="p-2 text-rose-500 hover:bg-rose-50 dark:hover:bg-rose-950/40 rounded-xl transition"
                      title="حذف کد"
                    >
                      <TrashIcon className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Tab 2: Users List */}
      {activeTab === 'users' && (
        <div className="bg-white dark:bg-slate-800 rounded-3xl p-6 shadow-sm border border-slate-100 dark:border-slate-700/60 space-y-4">
          <h2 className="text-base font-bold text-slate-800 dark:text-white pb-3 border-b border-slate-100 dark:border-slate-700 flex items-center gap-2">
            <UserGroupIcon className="w-5 h-5 text-blue-600" />
            لیست کاربران ثبت‌شده
          </h2>

          <div className="space-y-3">
            {users.map(u => (
              <div
                key={u.id}
                className="flex flex-col sm:flex-row sm:items-center justify-between p-4 rounded-2xl bg-slate-50 dark:bg-slate-900/40 border border-slate-100 dark:border-slate-700/60 gap-3"
              >
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-bold text-slate-800 dark:text-white">
                      {u.name}
                    </span>
                    {u.role === 'admin' ? (
                      <span className="px-2 py-0.5 rounded-md text-[10px] font-bold bg-purple-100 text-purple-700 dark:bg-purple-950 dark:text-purple-300">
                        مدیر
                      </span>
                    ) : (
                      <span className="px-2 py-0.5 rounded-md text-[10px] font-bold bg-slate-200 text-slate-700 dark:bg-slate-800 dark:text-slate-300">
                        کاربر
                      </span>
                    )}
                    {getStatusBadge(u.today_status)}
                    {u.telegram_id ? (
                      <span className="px-2 py-0.5 rounded-md text-[10px] font-bold bg-sky-100 text-sky-700 dark:bg-sky-950 dark:text-sky-300 flex items-center gap-1" title={`شناسه: ${u.telegram_id}`}>
                        <PaperAirplaneIcon className="w-2.5 h-2.5" />
                        تلگرام متصل
                      </span>
                    ) : (
                      <span className="px-2 py-0.5 rounded-md text-[10px] font-bold bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400">
                        بدون تلگرام
                      </span>
                    )}
                    {!u.is_active && (
                      <span className="px-2 py-0.5 rounded-md text-[10px] font-bold bg-rose-100 text-rose-700 dark:bg-rose-950 dark:text-rose-300">
                        غیرفعال
                      </span>
                    )}
                  </div>
                  <div className="text-[11px] text-slate-400 flex flex-wrap items-center gap-3">
                    <span>نام کاربری: {u.username || '-'}</span>
                    <span>شروع کار: {u.work_start}</span>
                    <span>سقف: {u.daily_hours}h</span>
                    <span>کارکرد این ماه: {formatHoursAndMinutes(u.month_worked_hours)}</span>
                  </div>
                </div>

                <div className="flex items-center gap-2 self-end sm:self-center">
                  <button
                    onClick={() => openEditUser(u)}
                    className="flex items-center gap-1 text-xs px-3 py-1.5 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-xl font-semibold transition"
                  >
                    <PencilSquareIcon className="w-3.5 h-3.5 text-blue-500" />
                    ویرایش
                  </button>

                  {/* Delete button (cannot delete currently logged-in user) */}
                  {currentUser?.id !== u.id && (
                    <button
                      onClick={() => handleDeleteUser(u)}
                      className="flex items-center gap-1 text-xs px-3 py-1.5 bg-rose-50 dark:bg-rose-950/40 border border-rose-200 dark:border-rose-800/60 text-rose-600 dark:text-rose-300 hover:bg-rose-100 dark:hover:bg-rose-900/60 rounded-xl font-semibold transition"
                      title="حذف کامل کاربر"
                    >
                      <TrashIcon className="w-3.5 h-3.5" />
                      حذف
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Create Invite Code Modal */}
      {showCreateInvite && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white dark:bg-slate-800 rounded-3xl max-w-sm w-full p-6 shadow-2xl border border-slate-100 dark:border-slate-700 space-y-4 animate-in fade-in zoom-in-95 duration-200">
            <h3 className="text-base font-bold text-slate-800 dark:text-white flex items-center gap-2">
              <KeyIcon className="w-5 h-5 text-purple-600" />
              ایجاد کد دعوت جدید
            </h3>

            <form onSubmit={handleCreateInvite} className="space-y-3">
              <div className="space-y-1">
                <label className="text-xs font-semibold text-slate-600 dark:text-slate-300">
                  ساعت شروع کار پیش‌فرض:
                </label>
                <input
                  type="time"
                  value={inviteStart}
                  onChange={e => setInviteStart(e.target.value)}
                  className="w-full bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl px-3 py-2 text-xs font-bold text-slate-800 dark:text-white outline-none focus:border-purple-500"
                  required
                />
              </div>

              <div className="space-y-1">
                <label className="text-xs font-semibold text-slate-600 dark:text-slate-300">
                  ساعت کارکرد روزانه:
                </label>
                <select
                  value={inviteHours}
                  onChange={e => setInviteHours(e.target.value)}
                  className="w-full bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl px-3 py-2 text-xs font-bold text-slate-800 dark:text-slate-100 outline-none focus:border-purple-500"
                >
                  {[4, 5, 6, 7, 7.5, 8, 8.5, 9, 10].map(h => (
                    <option key={h} value={h.toString()} className="bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100">{h} ساعت</option>
                  ))}
                </select>
              </div>

              <div className="space-y-1">
                <label className="text-xs font-semibold text-slate-600 dark:text-slate-300">
                  تعداد دفعات استفاده مجاز:
                </label>
                <input
                  type="number"
                  min="1"
                  max="50"
                  value={inviteMaxUses}
                  onChange={e => setInviteMaxUses(parseInt(e.target.value) || 1)}
                  className="w-full bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl px-3 py-2 text-xs font-bold text-slate-800 dark:text-white outline-none focus:border-purple-500"
                  required
                />
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setShowCreateInvite(false)}
                  className="text-xs px-3 py-2 text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-xl"
                >
                  انصراف
                </button>
                <button
                  type="submit"
                  disabled={creatingInvite}
                  className="text-xs px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white font-bold rounded-xl shadow-sm transition"
                >
                  {creatingInvite ? 'در حال ایجاد...' : 'ساخت کد'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit User Modal */}
      {editingUser && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white dark:bg-slate-800 rounded-3xl max-w-sm w-full p-6 shadow-2xl border border-slate-100 dark:border-slate-700 space-y-4 animate-in fade-in zoom-in-95 duration-200">
            <h3 className="text-base font-bold text-slate-800 dark:text-white flex items-center gap-2">
              <PencilSquareIcon className="w-5 h-5 text-blue-600" />
              ویرایش اطلاعات کاربر
            </h3>

            <form onSubmit={handleSaveUser} className="space-y-3">
              <div className="space-y-1">
                <label className="text-xs font-semibold text-slate-600 dark:text-slate-300">
                  نام و نام خانوادگی:
                </label>
                <input
                  type="text"
                  value={editName}
                  onChange={e => setEditName(e.target.value)}
                  className="w-full bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl px-3 py-2 text-xs font-semibold text-slate-800 dark:text-white outline-none focus:border-blue-500"
                  required
                />
              </div>

              <div className="space-y-1">
                <label className="text-xs font-semibold text-slate-600 dark:text-slate-300">
                  ساعت شروع کار مقرر:
                </label>
                <input
                  type="time"
                  value={editStart}
                  onChange={e => setEditStart(e.target.value)}
                  className="w-full bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl px-3 py-2 text-xs font-bold text-slate-800 dark:text-white outline-none focus:border-blue-500"
                  required
                />
              </div>

              <div className="space-y-1">
                <label className="text-xs font-semibold text-slate-600 dark:text-slate-300">
                  ساعت کارکرد روزانه:
                </label>
                <select
                  value={editHours}
                  onChange={e => setEditHours(parseFloat(e.target.value))}
                  className="w-full bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl px-3 py-2 text-xs font-bold text-slate-800 dark:text-slate-100 outline-none focus:border-blue-500"
                >
                  {[4, 5, 6, 7, 7.5, 8, 8.5, 9, 10].map(h => (
                    <option key={h} value={h} className="bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100">{h} ساعت</option>
                  ))}
                </select>
              </div>

              <div className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-700">
                <span className="text-xs font-bold text-slate-700 dark:text-slate-300">
                  وضعیت فعال بودن حساب:
                </span>
                <input
                  type="checkbox"
                  checked={editIsActive}
                  onChange={e => setEditIsActive(e.target.checked)}
                  className="w-4 h-4 rounded text-blue-600 cursor-pointer"
                />
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setEditingUser(null)}
                  className="text-xs px-3 py-2 text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-xl"
                >
                  انصراف
                </button>
                <button
                  type="submit"
                  disabled={savingUser}
                  className="text-xs px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-xl shadow-sm transition"
                >
                  {savingUser ? 'در حال ذخیره...' : 'ذخیره تغییرات'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Confirmation Dialog */}
      <ConfirmModal
        isOpen={confirmDialog.isOpen}
        title={confirmDialog.title}
        message={confirmDialog.message}
        confirmVariant={confirmDialog.confirmVariant}
        onConfirm={confirmDialog.onConfirm}
        onCancel={() => setConfirmDialog(prev => ({ ...prev, isOpen: false }))}
      />

      {/* Alert Dialog */}
      <AlertModal
        isOpen={alertDialog.isOpen}
        title={alertDialog.title}
        message={alertDialog.message}
        type={alertDialog.type}
        onClose={() => setAlertDialog(prev => ({ ...prev, isOpen: false }))}
      />
    </div>
  )
}

export default AdminPanel
